import { CustomerGroupCounters } from '@spike/email-marketing-model';

import { EmailCampaignFormValues } from './types';

export interface RecipientCountInputs {
    values: EmailCampaignFormValues;
    counters: CustomerGroupCounters | null;
    /**
     * Total-records read from the paginated customers fetch used for the
     * multi-tag union. Falls through as the base count when the user has
     * picked 2+ tags (the counters endpoint only exposes per-tag totals,
     * summing them would over-count clients tagged multiple times).
     */
    fetchedCustomersTotal: number;
}

/**
 * Single source of truth for "how many clients will receive this campaign
 * given the current draft config". Consumed by:
 *   - `TotalClientsBar` in Step 1 (visual)
 *   - Live quote fetch in `AddEmailCampaign.tsx`
 *   - `isStepValidFor` (Step 1 Continue gating)
 *
 * Returns `null` when the count is not yet computable — counters still
 * loading, or a maqueta / unknown recipientType. Callers decide how to
 * render null: the bar shows `-`, validation treats it as "not valid yet"
 * so Continue stays disabled.
 *
 * `counters.all` reflects whichever population was last fetched (customers /
 * leads / both), so `all_profiles`, `all_clients`, and `leads` all share the
 * same "counters.all − excludes" math — they only differ in which population
 * populated the counter upstream.
 *
 * Numeric returns are already exclusion-adjusted (`Math.max(0, base − excl)`),
 * so consumers never need to subtract again.
 */
export const getRecipientCount = ({ values, counters, fetchedCustomersTotal }: RecipientCountInputs): number | null => {
    // Include mode (positive selection from the picker drawer) short-circuits
    // the counter math — recipients ARE exactly the picked list. Independent
    // of criteria, tags, and exclusions.
    if (Array.isArray(values.selectedClientIds)) {
        return values.selectedClientIds.length;
    }

    // Exclude mode from here on. Without counters we can't compute anything.
    if (!counters) return null;

    if (
        values.recipientType === 'all_profiles' ||
        values.recipientType === 'all_clients' ||
        values.recipientType === 'leads'
    ) {
        return Math.max(0, counters.all - values.excludedClientIds.length);
    }

    if (values.recipientType === 'client_groups') {
        if (values.selectedTagIds.length === 0) return 0;
        const base =
            values.selectedTagIds.length === 1
                ? counters.byTag.find(entry => entry.tag === values.selectedTagIds[0])?.records ?? 0
                : fetchedCustomersTotal;
        return Math.max(0, base - values.excludedClientIds.length);
    }

    return null;
};
